import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { AdminActivityDay } from "../../../api/types";
import type { HostMetricsSeriesPoint } from "../../../hooks/useAdminHostMetricsWs";
import type { EChartsOption } from "echarts";

const CYAN = "#00bcd4";
const INDIGO = "#4858ab";

const HOST_RING_R = 88;
const HOST_RING_LEN = 2 * Math.PI * HOST_RING_R;

function sparkPathFromValues(values: number[]): string {
  const v =
    values.length >= 2 ? values : values.length === 1 ? [values[0], values[0]] : [0, 2, 0];
  const w = 100;
  const h = 40;
  const pad = 5;
  const min = Math.min(...v);
  const max = Math.max(...v);
  const span = max - min || 1;
  const pts = v.map((val, i) => {
    const x = (i / Math.max(1, v.length - 1)) * w;
    const y = h - pad - ((val - min) / span) * (h - 2 * pad);
    return [x, y] as const;
  });
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x, y] = pts[i];
    d += ` L ${x},${y}`;
  }
  return d;
}

/** 对齐 code.html：KPI 右侧 h-8 w-24、stroke-2、无 ECharts */
export function LuminousKpiSparkSvg({
  values,
  color,
}: {
  values: number[];
  color: "cyan" | "indigo";
}) {
  const stroke = color === "cyan" ? CYAN : INDIGO;
  const d = useMemo(() => sparkPathFromValues(values), [values]);

  return (
    <div className="luminous-kpi-spark" aria-hidden>
      <svg
        className="luminous-kpi-spark__svg"
        viewBox="0 0 100 40"
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={d} />
      </svg>
    </div>
  );
}

function weekdayZh(iso: string): string {
  const s = iso.includes("T") ? iso : `${iso}T12:00:00Z`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  const map = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  return map[d.getUTCDay()];
}

/** KPI 旁微型折线，青 / 靛 */
export function LuminousKpiSparkline({
  values,
  color,
}: {
  values: number[];
  color: "cyan" | "indigo";
}) {
  const stroke = color === "cyan" ? CYAN : INDIGO;
  const opt: EChartsOption = useMemo(() => {
    const v = values.length ? values : [0];
    return {
      grid: { left: 0, right: 0, top: 2, bottom: 2 },
      xAxis: { type: "category", show: false, data: v.map((_, i) => i) },
      yAxis: { type: "value", show: false, min: 0 },
      series: [
        {
          type: "line",
          data: v,
          smooth: 0.35,
          symbol: "none",
          lineStyle: { width: 2, color: stroke },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: color === "cyan" ? "rgba(0,188,212,0.25)" : "rgba(72,88,171,0.22)" },
                { offset: 1, color: "rgba(255,255,255,0)" },
              ],
            },
          },
        },
      ],
      animationDuration: 400,
    };
  }, [values, stroke, color]);

  return (
    <ReactECharts
      option={opt}
      style={{ width: 96, height: 32 }}
      opts={{ renderer: "canvas" }}
      notMerge
    />
  );
}

/** 业务趋势：双面积（支付订单 + 新注册），对齐设计稿青/靛 */
export function LuminousBusinessTrendDual({ points }: { points: AdminActivityDay[] }) {
  const option: EChartsOption = useMemo(() => {
    const labels = points.map((p) => weekdayZh(p.date));
    const orders = points.map((p) => p.orders_paid);
    const users = points.map((p) => p.new_users);

    return {
      color: [CYAN, INDIGO],
      grid: { left: 8, right: 12, top: 28, bottom: 28 },
      legend: {
        data: ["支付订单", "新注册用户"],
        right: 8,
        top: 0,
        textStyle: { color: "#3c494c", fontSize: 12 },
      },
      tooltip: { trigger: "axis", axisPointer: { type: "none" } },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: labels,
        axisLabel: { color: "rgba(60,73,76,0.45)", fontSize: 11, fontWeight: 700 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        splitLine: { show: false },
        axisLabel: { color: "#6c797c", fontSize: 10 },
      },
      series: [
        {
          name: "支付订单",
          type: "line",
          smooth: 0.35,
          showSymbol: false,
          lineStyle: { width: 3, color: CYAN },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(0,188,212,0.35)" },
                { offset: 1, color: "rgba(0,188,212,0.02)" },
              ],
            },
          },
          data: orders,
        },
        {
          name: "新注册用户",
          type: "line",
          smooth: 0.35,
          showSymbol: false,
          lineStyle: { width: 2.5, color: INDIGO },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(72,88,171,0.28)" },
                { offset: 1, color: "rgba(72,88,171,0.02)" },
              ],
            },
          },
          data: users,
        },
      ],
    };
  }, [points]);

  if (points.length === 0) {
    return (
      <div className="luminous-chart-empty">暂无近 7 日趋势数据</div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: 280, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
      lazyUpdate
    />
  );
}

function seriesTimeLabel(iso: string): string {
  if (!iso || iso.length < 16) return "—";
  return iso.slice(11, 19);
}

/** WebSocket 推送的服务器 CPU 占用趋势（内存用侧边环图单独展示，避免重复） */
export function LuminousHostCpuTrend({
  points,
  height = 320,
}: {
  points: HostMetricsSeriesPoint[];
  height?: number;
}) {
  const option: EChartsOption = useMemo(() => {
    const labels = points.map((p) => seriesTimeLabel(p.ts));
    const cpus = points.map((p) => p.cpu);

    return {
      color: [CYAN],
      grid: { left: 8, right: 12, top: 12, bottom: 28 },
      legend: { show: false },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "none" },
        valueFormatter: (v) => `${v}%`,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: labels,
        axisLabel: { color: "rgba(60,73,76,0.45)", fontSize: 11, fontWeight: 700 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        splitLine: { show: false },
        axisLabel: {
          color: "#6c797c",
          fontSize: 10,
          formatter: "{value}%",
        },
      },
      series: [
        {
          name: "CPU",
          type: "line",
          smooth: 0.35,
          showSymbol: points.length <= 12,
          symbolSize: 6,
          lineStyle: { width: 4, color: CYAN },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(0,188,212,0.35)" },
                { offset: 1, color: "rgba(0,188,212,0.02)" },
              ],
            },
          },
          data: cpus,
        },
      ],
    };
  }, [points]);

  if (points.length < 2) {
    return (
      <div className="luminous-chart-empty" style={{ minHeight: height }}>
        等待资源推送以绘制趋势…
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
      lazyUpdate
    />
  );
}

/** 与 code.html AI Usage Quota 同源 SVG 环形（192×192 / r=88 / stroke 12） */
export function LuminousHostMemoryDonut({
  percent,
  caption,
}: {
  percent: number;
  caption: string;
}) {
  const pct = Math.min(100, Math.max(0, Math.round(percent * 10) / 10));
  const dashOffset = HOST_RING_LEN * (1 - pct / 100);

  return (
    <div className="luminous-donut-spec">
      <div className="luminous-donut-spec__ring-wrap">
        <svg
          className="luminous-donut-spec__svg"
          viewBox="0 0 192 192"
          aria-hidden
        >
          <circle
            cx="96"
            cy="96"
            r={HOST_RING_R}
            fill="transparent"
            stroke={CYAN}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={HOST_RING_LEN}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 96 96)"
          />
        </svg>
        <div className="luminous-donut-spec__center">
          <span className="luminous-donut-spec__pct">{pct}%</span>
          <span className="luminous-donut-spec__sub">运行中</span>
        </div>
      </div>
      <p className="luminous-donut-spec__cap">{caption}</p>
    </div>
  );
}

/** AI 用量环形：真实 Token 占月度预算比例；无预算时用本月用量的视觉占位 */
export function LuminousAiQuotaDonut({
  usedMonth,
  budget,
}: {
  usedMonth: number;
  budget: number | null | undefined;
}) {
  const { pct, remainLabel } = useMemo(() => {
    if (budget != null && budget > 0) {
      const p = Math.min(100, Math.round((usedMonth / budget) * 1000) / 10);
      return { pct: p, remainLabel: `本月已用 ${usedMonth.toLocaleString("zh-CN")} / 预算 ${budget.toLocaleString("zh-CN")} Token` };
    }
    if (usedMonth <= 0) {
      return { pct: 0, remainLabel: "本月暂无 Token 消耗记录" };
    }
    const p = Math.min(95, 15 + Math.log10(usedMonth + 10) * 25);
    return {
      pct: Math.round(p * 10) / 10,
      remainLabel: `本月已用 ${usedMonth.toLocaleString("zh-CN")} Token（未配置月预算，占比为示意）`,
    };
  }, [usedMonth, budget]);

  const rest = Math.max(0, 100 - pct);
  const option: EChartsOption = useMemo(
    () => ({
      series: [
        {
          type: "pie",
          radius: ["58%", "78%"],
          center: ["50%", "50%"],
          silent: true,
          label: { show: false },
          itemStyle: { borderWidth: 0 },
          data: [
            { value: pct, name: "used", itemStyle: { color: CYAN } },
            { value: rest || 0.01, name: "rest", itemStyle: { color: "#e6e8ea" } },
          ],
        },
      ],
    }),
    [pct, rest],
  );

  return (
    <div className="luminous-donut-wrap">
      <div className="luminous-donut-chart">
        <ReactECharts
          option={option}
          style={{ height: 200, width: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge
        />
        <div className="luminous-donut-center">
          <span className="luminous-donut-pct">{pct}%</span>
          <span className="luminous-donut-sub">运行中</span>
        </div>
      </div>
      <p className="luminous-donut-caption">{remainLabel}</p>
    </div>
  );
}
